/**
 * AI Batting Diagnostics — shared section for the global /ai-analytics
 * page and the per-user PlayerAiAnalytics tab on /players/:id.
 *
 * Pass `userId` to scope every endpoint to a single player; omit for
 * the global view.
 *
 * Spec: Fixes to be done - after launch v0.1/AI-Analytics new updates/
 *       AI-BATTING-DIAGNOSTICS-IMPLEMENTATION.md §6.3-§6.4
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Progress,
  Row,
  Segmented,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  BarChartOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  TableOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Bar, Column, Heatmap, Scatter } from '@ant-design/charts';
import { adminApi } from '@/services/api';
import {
  shotName,
  deliveryName,
  zoneName,
  wicketTypeLabel,
  categoryName,
  shotTimingZoneName,
  shotTimingDirectionName,
  BATTING_SHOT_ZONES,
  DELIVERY_TYPE_TO_CATEGORY,
  SHOT_TYPES,
  SHOT_TIMING_ZONE,
  SHOT_TIMING_DIRECTION,
} from '@/constants/wicketConstants';
import { divisionName } from '@/constants/divisionConstants';
import { CopyableId } from './CopyableId';
import { useLatestRequest } from '@hooks/useLatestRequest';
import type {
  WicketsOverviewResponse,
  BounceClustersResponse,
  BatsmanPositionClustersResponse,
  CatchAnalysisResponse,
  WicketsByPhysicsResponse,
  WicketsExtrasResponse,
  WicketEventsResponse,
  WicketEventRow,
} from '@/types';

const { Title, Text, Paragraph } = Typography;

interface Props {
  /** When set, every endpoint is scoped to this user. Omit for the global view. */
  userId?: string;
  /** Period selector — passed through as { period, startDate?, endDate? }. */
  filters: Record<string, string | undefined>;
}

// ─── Consistent palette across every chart ────────────────────────────
const WICKET_COLORS: Record<string, string> = {
  bowled: '#5B8FF9', // blue
  caught: '#5AD8A6', // green
  lbw: '#F6655A', // red — alarm: exploit signal
  run_out: '#F6BD16', // amber
  stumped: '#9270CA', // purple
};
const ZONE_COLORS: Record<number, string> = {
  0: '#8c8c8c', // Neutral
  1: '#ff4d4f', // Hot — concerning when AI dismisses here
  2: '#1890ff', // Cold
};
const ZONE_DESCRIPTIONS: Record<number, string> = {
  0: 'No bonus / penalty',
  1: 'Batsman strength — AI should rarely take wickets here',
  2: 'Batsman weakness — wickets expected',
};

const wicketColor = (type: string): string => WICKET_COLORS[type] ?? '#bfbfbf';
const orderedWicketTypes = ['bowled', 'caught', 'lbw', 'run_out', 'stumped'] as const;

// ─── Small helpers ────────────────────────────────────────────────────
const fmt = (n: number) => n.toLocaleString();
const pct = (n: number, total: number) => (total === 0 ? 0 : Math.round((n / total) * 1000) / 10);
/** Share of `total` as a percentage string with exactly 3 decimals, e.g. "76.410". */
const pct3 = (n: number, total: number): string => (total === 0 ? 0 : (n / total) * 100).toFixed(3);
/** Count plus its share of `total`, e.g. "191,214 (76.410%)". */
const countWithPct = (n: number, total: number): string => `${fmt(n)} (${pct3(n, total)}%)`;
const sumCounts = (rows: ReadonlyArray<{ count: number }>): number =>
  rows.reduce((sum, r) => sum + r.count, 0);

/** Table cell: count followed by its share of `total` in muted text. */
const CountPct: React.FC<{ n: number; total: number }> = ({ n, total }) => (
  <span style={{ whiteSpace: 'nowrap' }}>
    {fmt(n)} <Text type="secondary">({pct3(n, total)}%)</Text>
  </span>
);

/** "% of total" table column: the row's `dataIndex` count as a share of `total`. */
const shareColumn = (total: number, dataIndex = 'count', title = '% of total') => ({
  title,
  key: `share-${dataIndex}`,
  dataIndex,
  align: 'right' as const,
  render: (v: number) => `${pct3(v, total)}%`,
});

// ─── Chart tooltips: count + share, precomputed onto each datum ──────
interface ShareDatum {
  /** Tooltip title: the bar / cell label (stacked charts add the bar's share of the chart). */
  tooltipTitle: string;
  /** Tooltip row name: the series for stacked charts, the measure for single-series ones. */
  tooltipName: string;
  /** Tooltip row value: "count (share%)". */
  shareLabel: string;
}

/**
 * G2 v5 tooltip that reads the labels precomputed by withChartShare /
 * withStackShare. Module-level so the callbacks keep a stable identity:
 * @ant-design/charts deep-compares its config and would re-render the
 * chart on every parent render otherwise. Items set no colour, so G2
 * keeps each element's (legend) colour on the row marker.
 */
const SHARE_TOOLTIP = {
  title: (d: ShareDatum) => d.tooltipTitle,
  items: [(d: ShareDatum) => ({ name: d.tooltipName, value: d.shareLabel })],
};

/** Single-series chart: each bar / cell as a share of the whole chart. */
function withChartShare<T extends { count: number }>(
  rows: T[],
  titleOf: (r: T) => string,
  nameOf: (r: T) => string
): Array<T & ShareDatum> {
  const total = sumCounts(rows);
  return rows.map((r) => ({
    ...r,
    tooltipTitle: titleOf(r),
    tooltipName: nameOf(r),
    shareLabel: countWithPct(r.count, total),
  }));
}

/**
 * Stacked chart: each segment as a share of its own bar (so a bar's
 * tooltip rows add up to 100%); the title carries the bar's share of
 * the whole chart.
 */
function withStackShare<T extends { bucket: string; count: number }>(
  rows: T[],
  nameOf: (r: T) => string
): Array<T & ShareDatum> {
  const total = sumCounts(rows);
  const barTotals = new Map<string, number>();
  for (const r of rows) barTotals.set(r.bucket, (barTotals.get(r.bucket) ?? 0) + r.count);
  return rows.map((r) => {
    const barTotal = barTotals.get(r.bucket) ?? 0;
    return {
      ...r,
      tooltipTitle: `${r.bucket} · ${pct3(barTotal, total)}% of all`,
      tooltipName: nameOf(r),
      shareLabel: countWithPct(r.count, barTotal),
    };
  });
}

const SectionHeader: React.FC<{ title: string; help?: string }> = ({ title, help }) => (
  <Space size={6} style={{ marginTop: 8 }}>
    <Title level={5} style={{ margin: 0 }}>
      {title}
    </Title>
    {help && (
      <Tooltip title={help}>
        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
      </Tooltip>
    )}
  </Space>
);

// ─── Chart-or-table panel — same dataset, two renderings ─────────────
interface StackedRow {
  bucket: string;
  type: string;
  rawType: string;
  count: number;
}

const StackedChartOrTable: React.FC<{
  data: StackedRow[];
  bucketLabel: string;
  mode: 'chart' | 'table';
  height?: number;
  axisRotate?: boolean;
}> = ({ data, bucketLabel, mode, height = 240, axisRotate = false }) => {
  const chartData = useMemo(() => withStackShare(data, (d) => d.type), [data]);
  if (data.length === 0) return <Empty />;

  if (mode === 'chart') {
    return (
      <Column
        data={chartData}
        xField="bucket"
        yField="count"
        colorField="type"
        stack
        legend={{ color: { position: 'top' } }}
        scale={{
          color: {
            domain: orderedWicketTypes.map(wicketTypeLabel),
            range: orderedWicketTypes.map((t) => wicketColor(t)),
          },
        }}
        height={height}
        axis={axisRotate ? { x: { labelAutoRotate: true } } : undefined}
        tooltip={SHARE_TOOLTIP}
      />
    );
  }

  // Table view — pivot rows to {bucket, bowled, caught, lbw, run_out, stumped, total}
  const grandTotal = sumCounts(data);
  const buckets = Array.from(new Set(data.map((d) => d.bucket)));
  const tableRows = buckets.map((b) => {
    const row: Record<string, unknown> = { bucket: b, total: 0 };
    for (const t of orderedWicketTypes) row[t] = 0;
    for (const d of data.filter((x) => x.bucket === b)) {
      row[d.rawType] = ((row[d.rawType] as number) ?? 0) + d.count;
      row.total = (row.total as number) + d.count;
    }
    return row;
  });

  return (
    <Table
      size="small"
      pagination={false}
      dataSource={tableRows}
      rowKey="bucket"
      columns={[
        { title: bucketLabel, dataIndex: 'bucket', width: 120 },
        ...orderedWicketTypes.map((t) => ({
          title: (
            <Tag color={wicketColor(t)} style={{ margin: 0 }}>
              {wicketTypeLabel(t)}
            </Tag>
          ),
          dataIndex: t,
          align: 'right' as const,
          // Share of this row (bucket), matching the chart tooltip.
          render: (v: number, row: Record<string, unknown>) =>
            v ? <CountPct n={v} total={row.total as number} /> : <Text type="secondary">-</Text>,
        })),
        {
          title: 'Total',
          dataIndex: 'total',
          align: 'right' as const,
          render: (v: number) => <strong>{fmt(v)}</strong>,
        },
        shareColumn(grandTotal, 'total'),
      ]}
    />
  );
};

// ─── Shot-timing QUALITY palette + chart/table helper ──────────────
// Unity TimingZone ordering (0..3): Perfect=green, Good=blue,
// OK=amber, Miss=red. Used everywhere the quality axis is stacked.
const TIMING_ZONE_COLORS: Record<number, string> = {
  0: '#52c41a', // Perfect — middled
  1: '#1890ff', // Good
  2: '#fa8c16', // OK — edged
  3: '#cf1322', // Miss — swung, no contact
};
const orderedTimingZones = [0, 1, 2, 3] as const;

// Shot-timing DIRECTION palette — Early/Center/Late.
const TIMING_DIRECTION_COLORS: Record<number, string> = {
  [-1]: '#fa8c16', // Early — amber
  0: '#52c41a', // Center — green
  1: '#cf1322', // Late — red
};

interface TimingZoneStackedRow {
  bucket: string;
  /** SHOT_TIMING_ZONE name (e.g. "Perfect"). */
  zone: string;
  /** Raw 0..3 ordinal so we can colour-lookup. */
  rawZone: number;
  count: number;
}

const TimingZoneStackedChartOrTable: React.FC<{
  data: TimingZoneStackedRow[];
  bucketLabel: string;
  mode: 'chart' | 'table';
  height?: number;
  axisRotate?: boolean;
}> = ({ data, bucketLabel, mode, height = 240, axisRotate = false }) => {
  const chartData = useMemo(() => withStackShare(data, (d) => d.zone), [data]);
  if (data.length === 0) return <Empty />;

  if (mode === 'chart') {
    return (
      <Column
        data={chartData}
        xField="bucket"
        yField="count"
        colorField="zone"
        stack
        legend={{ color: { position: 'top' } }}
        scale={{
          color: {
            domain: orderedTimingZones.map((s) => SHOT_TIMING_ZONE[s] ?? `Zone ${s}`),
            range: orderedTimingZones.map((s) => TIMING_ZONE_COLORS[s]),
          },
        }}
        height={height}
        axis={axisRotate ? { x: { labelAutoRotate: true } } : undefined}
        tooltip={SHARE_TOOLTIP}
      />
    );
  }

  // Table view — pivot rows to { bucket, Perfect, Good, OK, Miss, total }
  const grandTotal = sumCounts(data);
  const buckets = Array.from(new Set(data.map((d) => d.bucket)));
  const tableRows = buckets.map((b) => {
    const row: Record<string, unknown> = { bucket: b, total: 0 };
    for (const s of orderedTimingZones) row[String(s)] = 0;
    for (const d of data.filter((x) => x.bucket === b)) {
      const k = String(d.rawZone);
      row[k] = ((row[k] as number) ?? 0) + d.count;
      row.total = (row.total as number) + d.count;
    }
    return row;
  });

  return (
    <Table
      size="small"
      pagination={false}
      dataSource={tableRows}
      rowKey="bucket"
      columns={[
        { title: bucketLabel, dataIndex: 'bucket', width: 120 },
        ...orderedTimingZones.map((s) => ({
          title: (
            <Tag color={TIMING_ZONE_COLORS[s]} style={{ margin: 0 }}>
              {SHOT_TIMING_ZONE[s] ?? `Zone ${s}`}
            </Tag>
          ),
          dataIndex: String(s),
          align: 'right' as const,
          // Share of this row (bucket), matching the chart tooltip.
          render: (v: number, row: Record<string, unknown>) =>
            v ? <CountPct n={v} total={row.total as number} /> : <Text type="secondary">-</Text>,
        })),
        {
          title: 'Total',
          dataIndex: 'total',
          align: 'right' as const,
          render: (v: number) => <strong>{fmt(v)}</strong>,
        },
        shareColumn(grandTotal, 'total'),
      ]}
    />
  );
};

const DeliveryFamilyTag: React.FC<{ deliveryTypeId: number }> = ({ deliveryTypeId }) => {
  const fam = DELIVERY_TYPE_TO_CATEGORY[deliveryTypeId];
  if (fam === undefined) return null;
  const isSpin = fam === 0;
  return (
    <Tag color={isSpin ? 'purple' : 'blue'} style={{ marginLeft: 4 }}>
      {categoryName(fam)}
    </Tag>
  );
};

export const WicketDiagnostics: React.FC<Props> = ({ userId, filters }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<WicketsOverviewResponse | null>(null);
  const [bounce, setBounce] = useState<BounceClustersResponse | null>(null);
  const [batsmanPos, setBatsmanPos] = useState<BatsmanPositionClustersResponse | null>(null);
  const [catchAnalysis, setCatchAnalysis] = useState<CatchAnalysisResponse | null>(null);
  const [physics, setPhysics] = useState<WicketsByPhysicsResponse | null>(null);
  const [extras, setExtras] = useState<WicketsExtrasResponse | null>(null);
  const [events, setEvents] = useState<WicketEventsResponse | null>(null);
  const [eventsPage, setEventsPage] = useState({ page: 1, pageSize: 25 });
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  // A newer window (or page) makes a slower, older response stale.
  const beginAll = useLatestRequest();
  const beginEvents = useLatestRequest();

  const fetchAll = useCallback(async () => {
    const isLatest = beginAll();
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | undefined> = { ...filters };
      if (userId) params.userId = userId;
      const [o, b, bp, c, p, x] = await Promise.all([
        adminApi.getAiWicketsOverview(params),
        adminApi.getAiBounceClusters(params),
        adminApi.getAiBatsmanPositionClusters(params),
        adminApi.getAiCatchAnalysis(params),
        adminApi.getAiWicketsByPhysics(params),
        adminApi.getAiWicketsExtras(params),
      ]);
      if (!isLatest()) return;
      setOverview(o.data as WicketsOverviewResponse);
      setBounce(b.data as BounceClustersResponse);
      setBatsmanPos(bp.data as BatsmanPositionClustersResponse);
      setCatchAnalysis(c.data as CatchAnalysisResponse);
      setPhysics(p.data as WicketsByPhysicsResponse);
      setExtras(x.data as WicketsExtrasResponse);
    } catch (err) {
      if (!isLatest()) return;
      console.error('Failed to load wicket diagnostics', err);
      setError('Failed to load wicket diagnostics. Try again.');
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [userId, filters, beginAll]);

  /**
   * Raw events are heavy (up to 500 rows × 29 columns) and most
   * analysts never need them — gate behind an explicit user action
   * instead of loading on every page render. Pagination changes after
   * the first load re-fetch (same loader, different limit/offset).
   */
  const fetchEvents = useCallback(
    async (page: number, pageSize: number) => {
      const isLatest = beginEvents();
      setEventsLoading(true);
      setEventsError(null);
      try {
        const params: Record<string, string | number | undefined> = {
          ...filters,
        };
        if (userId) params.userId = userId;
        params.limit = pageSize;
        params.offset = (page - 1) * pageSize;
        const e = await adminApi.getAiWicketEvents(params);
        if (!isLatest()) return;
        setEvents(e.data as WicketEventsResponse);
      } catch (err) {
        if (!isLatest()) return;
        console.error('Failed to load wicket events', err);
        setEventsError('Failed to load raw wicket logs. Try again.');
      } finally {
        if (isLatest()) setEventsLoading(false);
      }
    },
    [filters, userId, beginEvents]
  );

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  // Filter/period/user changes invalidate any previously-loaded raw
  // events — drop them so the analyst gets a fresh fetch button instead
  // of silently stale data.
  useEffect(() => {
    beginEvents(); // a page still loading for the old window is now stale
    setEvents(null);
    setEventsPage({ page: 1, pageSize: 25 });
    setEventsError(null);
    setEventsLoading(false);
  }, [filters, userId, beginEvents]);

  // ─── Derived: per-wicket-type counts (from byDifficulty sum) ────────
  const dismissalMix = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const r of overview?.byDifficulty ?? []) {
      totals[r.wicketType] = (totals[r.wicketType] ?? 0) + r.count;
    }
    const total = Object.values(totals).reduce((a, b) => a + b, 0);
    return orderedWicketTypes.map((type) => ({
      type,
      count: totals[type] ?? 0,
      pctLabel: pct3(totals[type] ?? 0, total),
    }));
  }, [overview]);

  // ─── Derived: overall LBW % across the cohort ───────────────────────
  const overallLbwPct = useMemo(() => {
    const total = overview?.totalWickets ?? 0;
    const lbw = dismissalMix.find((d) => d.type === 'lbw')?.count ?? 0;
    // Unrounded, so the warning fires on the same value its 3-decimal label shows.
    return total === 0 ? 0 : (lbw / total) * 100;
  }, [dismissalMix, overview]);
  const lbwMix = dismissalMix.find((d) => d.type === 'lbw');

  // ─── Derived: single-series chart data with precomputed share labels ─
  const angleChart = useMemo(
    () =>
      withChartShare(
        (catchAnalysis?.angleBuckets ?? []).map((b) => ({
          bucket: `${b.bucketStart}° to ${b.bucketEnd}°`,
          count: b.count,
        })),
        (r) => r.bucket,
        () => 'Catches'
      ),
    [catchAnalysis]
  );
  const fieldingChart = useMemo(
    () =>
      withChartShare(
        (catchAnalysis?.fieldingZones ?? []).map((r) => ({
          zone: r.zoneName || zoneName(r.zoneId),
          count: r.count,
        })),
        (r) => r.zone,
        () => 'Catches'
      ),
    [catchAnalysis]
  );
  const travelChart = useMemo(
    () =>
      withChartShare(
        (catchAnalysis?.travelDistanceBuckets ?? []).map((b) => ({
          bucket: `${b.bucketStart}-${b.bucketEnd}m`,
          count: b.count,
        })),
        (r) => r.bucket,
        () => 'Catches'
      ),
    [catchAnalysis]
  );
  const overChart = useMemo(
    () =>
      withChartShare(
        (extras?.byOver ?? []).map((r) => ({
          over: `Over ${r.over + 1}`,
          count: r.count,
        })),
        (r) => r.over,
        () => 'Wickets'
      ),
    [extras]
  );
  const bounceChart = useMemo(
    () =>
      withChartShare(
        (bounce?.cells ?? []).map((c) => ({
          x: c.x,
          z: c.z,
          count: c.count,
          type: wicketTypeLabel(c.wicketType),
        })),
        (r) => `X ${r.x.toFixed(1)} m, Z ${r.z.toFixed(1)} m`,
        (r) => r.type
      ),
    [bounce]
  );
  const batsmanPosChart = useMemo(
    () =>
      withChartShare(
        (batsmanPos?.cells ?? []).map((c) => ({
          x: c.x,
          z: c.z,
          count: c.count,
          type: wicketTypeLabel(c.wicketType),
        })),
        (r) => `X ${r.x.toFixed(1)} m, Z ${r.z.toFixed(1)} m`,
        (r) => r.type
      ),
    [batsmanPos]
  );

  // ─── Derived: shot type (Grounded / Lofted) summed across wicket types ─
  const shotTypeMix = useMemo(() => {
    const rows = (extras?.byShotType ?? []).reduce<
      Array<{ shotType: number | null; count: number }>
    >((acc, r) => {
      const found = acc.find((x) => x.shotType === r.shotType);
      if (found) found.count += r.count;
      else acc.push({ shotType: r.shotType, count: r.count });
      return acc;
    }, []);
    return { rows, total: sumCounts(rows) };
  }, [extras]);

  // ─── Derived: per-division totals for the division table's "% of division" ─
  const divisionTotals = useMemo(() => {
    const totals = new Map<number, number>();
    for (const r of overview?.byDivision ?? []) {
      totals.set(r.division, (totals.get(r.division) ?? 0) + r.count);
    }
    return totals;
  }, [overview]);

  // ─── Loading / error / empty ────────────────────────────────────────
  if (loading && !overview) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }
  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }
  const totalWickets = overview?.totalWickets ?? 0;
  if (totalWickets === 0) {
    return (
      <Card title="Wicket Diagnostics">
        <Empty
          description={
            <Text type="secondary">
              No wicket events yet for this period. Unity needs to ship the build that emits the new{' '}
              <code>wickets[]</code> array (see{' '}
              <code>AI-BATTING-DIAGNOSTICS-IMPLEMENTATION.md</code> §2 and §10 for the open Unity
              items).
            </Text>
          }
        />
      </Card>
    );
  }

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Header */}
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              Wicket Diagnostics
            </Title>
            <Text type="secondary">
              Where, why, and how the AI batsman gets out. <strong>{fmt(totalWickets)}</strong>{' '}
              events in this period.
            </Text>
          </Col>
          <Col>
            <Segmented
              value={viewMode}
              onChange={(v) => setViewMode(v as 'chart' | 'table')}
              options={[
                { label: 'Charts', value: 'chart', icon: <BarChartOutlined /> },
                { label: 'Tables', value: 'table', icon: <TableOutlined /> },
              ]}
            />
          </Col>
        </Row>

        {userId && (
          <Alert
            type="info"
            showIcon
            icon={<UserOutlined />}
            message={
              <Space size={4} wrap>
                <span>Per-player view — analytics scoped to user</span>
                <CopyableId value={userId} previewLength={8} />
                <span>
                  . For the cohort-wide view, open the main <strong>AI Analytics</strong> module.
                </span>
              </Space>
            }
          />
        )}

        {/* ── 1. Dismissal mix — first thing you should look at ───────── */}
        <Card size="small" title="Dismissal mix">
          <Row gutter={[12, 12]}>
            {dismissalMix.map((d) => (
              <Col xs={12} sm={8} md={4} key={d.type}>
                <Card
                  size="small"
                  style={{
                    borderTop: `3px solid ${wicketColor(d.type)}`,
                    background: '#fafafa',
                  }}
                >
                  <Statistic
                    title={wicketTypeLabel(d.type)}
                    value={d.count}
                    suffix={
                      <span style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 4 }}>
                        ({d.pctLabel}%)
                      </span>
                    }
                    valueStyle={{ color: wicketColor(d.type), fontSize: 22 }}
                  />
                </Card>
              </Col>
            ))}
          </Row>

          {overallLbwPct >= 30 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message={`LBW dismissals are ${lbwMix?.pctLabel ?? '0.000'}% of all wickets`}
              description="Brief flag: sustained LBW above ~30% (especially in Godly/Legendary tiers) often indicates an AI animation or detection exploit. Drill into the AI-difficulty section below."
            />
          )}
        </Card>

        {/* ── 2. Ball physics — what kind of delivery causes wickets ──── */}
        <Card
          size="small"
          title={
            <SectionHeader
              title="Ball physics"
              help="Wicket type stacked by ball physics. Stack heights show volume; colors compare dismissal modes within each bucket."
            />
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Text strong>By pace (km/h)</Text>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="Pace bucket"
                data={(physics?.pace ?? []).map((r) => ({
                  bucket: r.bucket,
                  count: r.count,
                  type: wicketTypeLabel(r.wicketType),
                  rawType: r.wicketType,
                }))}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>By spin intensity</Text>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="Spin bucket"
                data={(physics?.spin ?? []).map((r) => ({
                  bucket: r.bucket,
                  count: r.count,
                  type: wicketTypeLabel(r.wicketType),
                  rawType: r.wicketType,
                }))}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>By swing intensity</Text>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="Swing bucket"
                data={(physics?.swing ?? []).map((r) => ({
                  bucket: r.bucket,
                  count: r.count,
                  type: wicketTypeLabel(r.wicketType),
                  rawType: r.wicketType,
                }))}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>By delivery type</Text>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="Delivery"
                axisRotate
                data={(physics?.delivery ?? []).map((r) => ({
                  bucket: deliveryName(r.deliveryTypeId),
                  count: r.count,
                  type: wicketTypeLabel(r.wicketType),
                  rawType: r.wicketType,
                }))}
              />
            </Col>
          </Row>
        </Card>

        {/* ── 3. Where on the pitch / crease ────────────────────────────── */}
        <Card
          size="small"
          title={
            <SectionHeader
              title="Where the wicket happened"
              help="Heatmap = ball first-bounce on the pitch (10cm grid). Scatter = batsman feet position at dismissal."
            />
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Text strong>Ball bounce on the pitch</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                X = lateral offset from middle stump (m), Z = distance from batsman (m)
              </div>
              {bounce && bounce.cells.length > 0 ? (
                viewMode === 'chart' ? (
                  <Heatmap
                    data={bounceChart}
                    xField="x"
                    yField="z"
                    colorField="count"
                    shapeField="square"
                    height={320}
                    tooltip={SHARE_TOOLTIP}
                  />
                ) : (
                  <Table
                    size="small"
                    scroll={{ y: 280 }}
                    pagination={false}
                    dataSource={bounce.cells}
                    rowKey={(r) => `${r.x}-${r.z}-${r.wicketType}`}
                    columns={[
                      {
                        title: 'X (m)',
                        dataIndex: 'x',
                        align: 'right',
                        render: (v: number) => v.toFixed(1),
                      },
                      {
                        title: 'Z (m)',
                        dataIndex: 'z',
                        align: 'right',
                        render: (v: number) => v.toFixed(1),
                      },
                      {
                        title: 'Type',
                        dataIndex: 'wicketType',
                        render: (v: string) => (
                          <Tag color={wicketColor(v)}>{wicketTypeLabel(v)}</Tag>
                        ),
                      },
                      {
                        title: 'Count',
                        dataIndex: 'count',
                        align: 'right',
                        render: (v: number) => fmt(v),
                      },
                      shareColumn(sumCounts(bounce.cells)),
                    ]}
                  />
                )
              ) : (
                <Empty />
              )}
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>Batsman position at dismissal</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Marker color = dismissal mode; size = number of wickets at that exact position
              </div>
              {batsmanPos && batsmanPos.cells.length > 0 ? (
                viewMode === 'chart' ? (
                  <Scatter
                    data={batsmanPosChart}
                    xField="x"
                    yField="z"
                    colorField="type"
                    sizeField="count"
                    legend={{ color: { position: 'top' } }}
                    scale={{
                      color: {
                        domain: orderedWicketTypes.map(wicketTypeLabel),
                        range: orderedWicketTypes.map((t) => wicketColor(t)),
                      },
                    }}
                    height={320}
                    tooltip={SHARE_TOOLTIP}
                  />
                ) : (
                  <Table
                    size="small"
                    scroll={{ y: 280 }}
                    pagination={false}
                    dataSource={batsmanPos.cells}
                    rowKey={(r) => `${r.x}-${r.z}-${r.wicketType}-${r.shotId ?? 'na'}`}
                    columns={[
                      {
                        title: 'X (m)',
                        dataIndex: 'x',
                        align: 'right',
                        render: (v: number) => v.toFixed(1),
                      },
                      {
                        title: 'Z (m)',
                        dataIndex: 'z',
                        align: 'right',
                        render: (v: number) => v.toFixed(1),
                      },
                      {
                        title: 'Type',
                        dataIndex: 'wicketType',
                        render: (v: string) => (
                          <Tag color={wicketColor(v)}>{wicketTypeLabel(v)}</Tag>
                        ),
                      },
                      {
                        title: 'Shot',
                        dataIndex: 'shotId',
                        render: (v: number | null) =>
                          v == null ? <Text type="secondary">-</Text> : <Tag>{shotName(v)}</Tag>,
                      },
                      {
                        title: 'Count',
                        dataIndex: 'count',
                        align: 'right',
                        render: (v: number) => fmt(v),
                      },
                      shareColumn(sumCounts(batsmanPos.cells)),
                    ]}
                  />
                )
              ) : (
                <Empty />
              )}
            </Col>
          </Row>
        </Card>

        {/* ── 4. Shot patterns + Hot/Neutral/Cold zone ──────────────────── */}
        <Card
          size="small"
          title={
            <SectionHeader
              title="Shot patterns & batting zones"
              help="Which shot identities and which player strength/weakness zones produce the most wickets."
            />
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Text strong>Top shots leading to wickets</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Shot identity from Unity <code>shots</code> enum. LBW count called out separately.
              </div>
              {overview && overview.topShotIdsForLbw.length > 0 ? (
                <Table
                  size="small"
                  pagination={false}
                  dataSource={overview.topShotIdsForLbw}
                  rowKey="shotId"
                  columns={[
                    {
                      title: 'Shot',
                      dataIndex: 'shotId',
                      render: (id: number, row) => (
                        <Space size={4} wrap={false} style={{ maxWidth: '100%' }}>
                          <Tooltip title={`shotId ${id}`}>
                            <Tag
                              style={{
                                margin: 0,
                                maxWidth: 220,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'inline-block',
                                verticalAlign: 'middle',
                              }}
                            >
                              {row.shotName || shotName(id)}
                            </Tag>
                          </Tooltip>
                          <CopyableId
                            value={String(id)}
                            previewLength={String(id).length}
                            showEllipsis={false}
                          />
                        </Space>
                      ),
                    },
                    {
                      title: 'Total wickets',
                      dataIndex: 'count',
                      width: 120,
                      align: 'right',
                      render: (v: number) => fmt(v),
                    },
                    // Top-10 list, so shares are of ALL wickets / ALL LBWs, not of the rows shown.
                    shareColumn(totalWickets),
                    {
                      title: 'LBW',
                      dataIndex: 'lbwCount',
                      width: 100,
                      align: 'right',
                      render: (v: number) =>
                        v > 0 ? (
                          <Tag color={WICKET_COLORS.lbw}>{fmt(v)}</Tag>
                        ) : (
                          <Text type="secondary">0</Text>
                        ),
                    },
                    shareColumn(lbwMix?.count ?? 0, 'lbwCount', '% of all LBW'),
                  ]}
                />
              ) : (
                <Empty />
              )}
            </Col>
            <Col xs={24} lg={8}>
              <Text strong>Batting shot zone</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                Per-player heatmap zones (Unity <code>ZoneType</code>).
              </div>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {[0, 1, 2].map((zoneId) => {
                  const value = overview?.zoneBreakdown?.[zoneId] ?? 0;
                  const fraction = pct3(value, totalWickets);
                  return (
                    <Card key={zoneId} size="small" bodyStyle={{ padding: '8px 12px' }}>
                      <Row align="middle" gutter={8}>
                        <Col flex="auto">
                          <Text strong style={{ color: ZONE_COLORS[zoneId] }}>
                            {BATTING_SHOT_ZONES[zoneId]}
                          </Text>
                          <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            {ZONE_DESCRIPTIONS[zoneId]}
                          </div>
                        </Col>
                        <Col>
                          <Statistic
                            value={value}
                            suffix={
                              <span style={{ fontSize: 12, color: '#8c8c8c' }}>({fraction}%)</span>
                            }
                            valueStyle={{ color: ZONE_COLORS[zoneId], fontSize: 18 }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </Space>
              {(overview?.zoneBreakdown?.[1] ?? 0) > totalWickets * 0.3 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                  message="Hot-zone dismissals high"
                  description="More than 30% of wickets are in the batsman's strong (Hot) zone. Likely AI logic or animation gap."
                />
              )}
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0 12px' }} />

          {/* Top delivery types with category chip */}
          <Text strong>Top deliveries leading to wickets</Text>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
            Unity <code>BowlingTypeFlat</code> id. Pace family 0-7, spin family 10-17.
          </div>
          {overview && overview.topDeliveryTypesForWickets.length > 0 ? (
            <Table
              size="small"
              pagination={false}
              dataSource={overview.topDeliveryTypesForWickets}
              rowKey="deliveryTypeId"
              columns={[
                {
                  title: 'Delivery',
                  render: (_v, row) => (
                    <>
                      <Tag>{row.deliveryName || deliveryName(row.deliveryTypeId)}</Tag>
                      <DeliveryFamilyTag deliveryTypeId={row.deliveryTypeId} />
                    </>
                  ),
                },
                {
                  title: 'Wickets',
                  dataIndex: 'count',
                  width: 140,
                  align: 'right',
                  render: (v: number) => (
                    <Progress
                      percent={pct(v, overview.topDeliveryTypesForWickets[0]?.count || v)}
                      size="small"
                      format={() => fmt(v)}
                      strokeColor="#1890ff"
                    />
                  ),
                },
                // Top-10 list, so the share is of ALL wickets, not of the rows shown.
                shareColumn(totalWickets),
              ]}
            />
          ) : (
            <Empty />
          )}
        </Card>

        {/* ── 5. Catches drill-down (only if there are catches) ──────────── */}
        {catchAnalysis && catchAnalysis.totalCatches > 0 && (
          <Card
            size="small"
            title={
              <SectionHeader
                title={`Catches (${fmt(catchAnalysis.totalCatches)} dismissals)`}
                help="Catch-only fields: shot angle, wagon-wheel direction, and the distance the fielder had to travel."
              />
            }
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={8}>
                <Text strong>Shot angle</Text>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                  Negative = off side; positive = leg side
                </div>
                {catchAnalysis.angleBuckets.length > 0 ? (
                  viewMode === 'chart' ? (
                    <Column
                      data={angleChart}
                      xField="bucket"
                      yField="count"
                      height={220}
                      axis={{ x: { labelAutoRotate: true } }}
                      tooltip={SHARE_TOOLTIP}
                    />
                  ) : (
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={catchAnalysis.angleBuckets}
                      rowKey={(r) => `${r.bucketStart}-${r.bucketEnd}`}
                      columns={[
                        {
                          title: 'Angle range',
                          render: (_v, r) => `${r.bucketStart}° to ${r.bucketEnd}°`,
                        },
                        {
                          title: 'Catches',
                          dataIndex: 'count',
                          align: 'right',
                          render: (v: number) => fmt(v),
                        },
                        shareColumn(sumCounts(catchAnalysis.angleBuckets)),
                      ]}
                    />
                  )
                ) : (
                  <Empty />
                )}
              </Col>
              <Col xs={24} lg={8}>
                <Text strong>Fielding direction</Text>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                  Wagon-wheel wedges (Unity <code>Segment</code> 1-10)
                </div>
                {catchAnalysis.fieldingZones.length > 0 ? (
                  viewMode === 'chart' ? (
                    <Bar
                      data={fieldingChart}
                      xField="count"
                      yField="zone"
                      legend={false}
                      height={220}
                      tooltip={SHARE_TOOLTIP}
                    />
                  ) : (
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={catchAnalysis.fieldingZones}
                      rowKey="zoneId"
                      columns={[
                        {
                          title: 'Zone',
                          render: (_v, r) => <Tag>{r.zoneName || zoneName(r.zoneId)}</Tag>,
                        },
                        {
                          title: 'Catches',
                          dataIndex: 'count',
                          align: 'right',
                          render: (v: number) => fmt(v),
                        },
                        shareColumn(sumCounts(catchAnalysis.fieldingZones)),
                      ]}
                    />
                  )
                ) : (
                  <Empty />
                )}
              </Col>
              <Col xs={24} lg={8}>
                <Text strong>Fielder travel distance</Text>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                  Distance from catch start to catch position (m)
                  {catchAnalysis.implausibleTravelMetres != null && (
                    <>
                      {' '}
                      · {fmt(catchAnalysis.implausibleTravelCount ?? 0)} over{' '}
                      {catchAnalysis.implausibleTravelMetres} m left out as implausible (bad
                      position data)
                    </>
                  )}
                </div>
                {catchAnalysis.travelDistanceBuckets.length > 0 ? (
                  viewMode === 'chart' ? (
                    <Column
                      data={travelChart}
                      xField="bucket"
                      yField="count"
                      height={220}
                      tooltip={SHARE_TOOLTIP}
                    />
                  ) : (
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={catchAnalysis.travelDistanceBuckets}
                      rowKey={(r) => `${r.bucketStart}-${r.bucketEnd}`}
                      columns={[
                        {
                          title: 'Distance',
                          render: (_v, r) => `${r.bucketStart}-${r.bucketEnd}m`,
                        },
                        {
                          title: 'Catches',
                          dataIndex: 'count',
                          align: 'right',
                          render: (v: number) => fmt(v),
                        },
                        shareColumn(sumCounts(catchAnalysis.travelDistanceBuckets)),
                      ]}
                    />
                  )
                ) : (
                  <Empty description="No catch travel data" />
                )}
              </Col>
            </Row>
          </Card>
        )}

        {/* ── 6. AI difficulty drill-down — the exploit-hunt section ────── */}
        <Card
          size="small"
          title={
            <SectionHeader
              title="By AI difficulty"
              help="The brief flagged ~60-70% LBW dismissals in Godly/Legendary tiers as a likely AI exploit. The aidiff column is the raw director difficulty score (Unity to confirm tier→label mapping)."
            />
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Text strong>LBW % by difficulty tier</Text>
              {(overview?.lbwPctByTier ?? []).length === 0 ? (
                <Empty />
              ) : (
                <Table
                  size="small"
                  dataSource={overview?.lbwPctByTier}
                  rowKey={(r) => String(r.aidiff)}
                  pagination={false}
                  columns={[
                    {
                      title: 'Difficulty',
                      dataIndex: 'aidiff',
                      width: 100,
                      render: (v: number) =>
                        v == null ? <Tag>unknown</Tag> : <Tag>aidiff {v}</Tag>,
                    },
                    {
                      title: 'Wickets',
                      dataIndex: 'totalWickets',
                      width: 90,
                      align: 'right',
                      render: (v: number) => fmt(v),
                    },
                    shareColumn(
                      (overview?.lbwPctByTier ?? []).reduce((sum, r) => sum + r.totalWickets, 0),
                      'totalWickets'
                    ),
                    {
                      title: 'LBW',
                      dataIndex: 'lbwCount',
                      width: 80,
                      align: 'right',
                      render: (v: number) =>
                        v > 0 ? (
                          <Tag color={WICKET_COLORS.lbw}>{fmt(v)}</Tag>
                        ) : (
                          <Text type="secondary">0</Text>
                        ),
                    },
                    {
                      title: 'LBW %',
                      dataIndex: 'lbwPct',
                      // Bar length keeps the server %; the label is the tier's LBW share, 3 dp.
                      render: (v: number, r) => (
                        <Progress
                          percent={v}
                          size="small"
                          format={() => `${pct3(r.lbwCount, r.totalWickets)}%`}
                          strokeColor={v >= 60 ? '#ff4d4f' : v >= 30 ? '#faad14' : '#52c41a'}
                        />
                      ),
                    },
                  ]}
                />
              )}
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>Wicket type by difficulty</Text>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="Difficulty"
                axisRotate
                height={300}
                data={(overview?.byDifficulty ?? []).map((r) => ({
                  bucket: r.aidiff == null ? 'unknown' : `aidiff ${r.aidiff}`,
                  count: r.count,
                  type: wicketTypeLabel(r.wicketType),
                  rawType: r.wicketType,
                }))}
              />
            </Col>
          </Row>
        </Card>

        {/* ── 7. Division breakdown ─────────────────────────────────────── */}
        <Card
          size="small"
          title={
            <SectionHeader
              title="By division"
              help="One row per (division, wicket type). Use this to spot tiered anomalies (eg. a single division producing all the LBWs)."
            />
          }
        >
          {overview && overview.byDivision.length > 0 ? (
            viewMode === 'chart' ? (
              <StackedChartOrTable
                mode="chart"
                bucketLabel="Division"
                axisRotate
                height={320}
                data={overview.byDivision.map((r) => ({
                  bucket: divisionName(r.division),
                  count: r.count,
                  type: wicketTypeLabel(r.wicketType),
                  rawType: r.wicketType,
                }))}
              />
            ) : (
              <Table
                size="small"
                dataSource={overview.byDivision}
                rowKey={(r) => `${r.division}-${r.wicketType}`}
                pagination={{ pageSize: 20, size: 'small' }}
                columns={[
                  {
                    title: 'Division',
                    dataIndex: 'division',
                    width: 200,
                    render: (v: number) => <Tag>{divisionName(v)}</Tag>,
                  },
                  {
                    title: 'Dismissal',
                    dataIndex: 'wicketType',
                    width: 140,
                    render: (v: string) => <Tag color={wicketColor(v)}>{wicketTypeLabel(v)}</Tag>,
                  },
                  {
                    title: 'Count',
                    dataIndex: 'count',
                    align: 'right',
                    render: (v: number) => fmt(v),
                  },
                  {
                    title: '% of division',
                    key: 'share-division',
                    dataIndex: 'count',
                    align: 'right',
                    render: (v: number, r) => `${pct3(v, divisionTotals.get(r.division) ?? 0)}%`,
                  },
                  shareColumn(sumCounts(overview.byDivision)),
                ]}
              />
            )
          ) : (
            <Empty />
          )}
        </Card>

        {/* ── 8. Extras — innings / over / batsman idx / bowler idx / shot type ─ */}
        <Card
          size="small"
          title={
            <SectionHeader
              title="Innings, over, squad index, shot type"
              help="The five columns that aren't part of the headline charts above. Useful for spotting innings-bias, over-window patterns, or which squad slot is most exploitable."
            />
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Text strong>By innings</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Innings 1 = user batting (AI bowling); 2 = AI batting (user bowling)
              </div>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="Innings"
                data={(extras?.byInnings ?? []).map((r) => ({
                  bucket: r.innings === 1 ? '1 (user bat)' : '2 (AI bat)',
                  count: r.count,
                  type: wicketTypeLabel(r.wicketType),
                  rawType: r.wicketType,
                }))}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>Over progression</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Wickets per over (0-based). Spikes at overs 0-2 suggest powerplay vulnerability;
                spikes near the end suggest death-over collapses.
              </div>
              {extras && extras.byOver.length > 0 ? (
                viewMode === 'chart' ? (
                  <Column
                    data={overChart}
                    xField="over"
                    yField="count"
                    height={220}
                    tooltip={SHARE_TOOLTIP}
                  />
                ) : (
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={extras.byOver}
                    rowKey="over"
                    columns={[
                      { title: 'Over', dataIndex: 'over', render: (v: number) => `Over ${v + 1}` },
                      {
                        title: 'Wickets',
                        dataIndex: 'count',
                        align: 'right',
                        render: (v: number) => fmt(v),
                      },
                      shareColumn(sumCounts(extras.byOver)),
                    ]}
                  />
                )
              ) : (
                <Empty />
              )}
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>By AI batsman position (squad index 0-10)</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Which slot in the AI line-up loses wickets — top of order vs tail.
              </div>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="AI batsman slot"
                data={(extras?.byBatsmanIndex ?? []).map((r) => ({
                  bucket: `#${r.squadIndex + 1}`,
                  count: r.count,
                  type: wicketTypeLabel(r.wicketType),
                  rawType: r.wicketType,
                }))}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>By user bowler position (squad index 0-10)</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Which user bowler is most effective at taking AI wickets.
              </div>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="User bowler slot"
                data={(extras?.byBowlerIndex ?? []).map((r) => ({
                  bucket: `#${r.squadIndex + 1}`,
                  count: r.count,
                  type: wicketTypeLabel(r.wicketType),
                  rawType: r.wicketType,
                }))}
              />
            </Col>
            <Col xs={24}>
              <Text strong>By shot type (Grounded / Lofted)</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Unity <code>ShotElevation</code>. Grounded vs Lofted dismissal mix. "Both" =
                capability flag (Grounded|Lofted) from capability-mode emitters. "n/a" = no shot
                attempted (bowled / lbw / run-out without playing a shot).
              </div>
              {shotTypeMix.rows.length > 0 ? (
                <Row gutter={[12, 12]}>
                  {shotTypeMix.rows.map((r) => (
                    <Col xs={12} sm={6} md={4} key={String(r.shotType)}>
                      <Card size="small">
                        <Statistic
                          title={
                            r.shotType == null
                              ? 'n/a (no shot)'
                              : (SHOT_TYPES[r.shotType] ?? `Shot ${r.shotType}`)
                          }
                          value={r.count}
                          suffix={
                            <span style={{ fontSize: 11, color: '#8c8c8c', marginLeft: 4 }}>
                              ({pct3(r.count, shotTypeMix.total)}%)
                            </span>
                          }
                          valueStyle={{ fontSize: 18 }}
                        />
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Empty />
              )}
            </Col>
          </Row>
        </Card>

        {/* ── 8b. Shot Timing — meter quality across physics/zone/delivery ─ */}
        <Card
          size="small"
          title={
            <SectionHeader
              title="Shot timing meter"
              help="WCC4 AI Batting brief §13 — quality + direction the timing meter resolved to at shot execution. Quality (Unity TimingZone): Perfect (middled), Good, OK (edged), Miss (whiffed). Direction (Unity TimingDirection): Early / Center / Late. The two axes are independent — a real shot is both."
            />
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Text strong>Wicket type by timing zone (quality)</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Unity <code>TimingZone</code> — how the bat met the ball. A Hot-zone shot that
                resolves to <em>OK</em> or <em>Miss</em>
                tells you the shot selection was right but execution failed.
              </div>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="Timing zone"
                data={(extras?.byShotTimingZone ?? [])
                  .filter((r) => r.shotTimingZone != null)
                  .map((r) => ({
                    bucket:
                      SHOT_TIMING_ZONE[r.shotTimingZone as number] ?? `Zone ${r.shotTimingZone}`,
                    count: r.count,
                    type: wicketTypeLabel(r.wicketType),
                    rawType: r.wicketType,
                  }))}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>Wicket type by timing direction</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Unity <code>TimingDirection</code> — which side of perfect the bat fired. Orthogonal
                to zone (a Good shot can still be Late). A Late-heavy stack on bowled/LBW signals
                reaction-time gaps.
              </div>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="Direction"
                data={(extras?.byShotTimingDirection ?? [])
                  .filter((r) => r.shotTimingDirection != null)
                  .map((r) => ({
                    bucket:
                      SHOT_TIMING_DIRECTION[r.shotTimingDirection as number] ??
                      `Dir ${r.shotTimingDirection}`,
                    count: r.count,
                    type: wicketTypeLabel(r.wicketType),
                    rawType: r.wicketType,
                  }))}
              />
            </Col>

            <Col xs={24}>
              <Text strong>Wicket type by timing-value bucket</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Continuous reading bucketed into 10 bins over the SIGNED [-1..+1] range. Buckets 1–5
                are <em>early-side</em>; 6 straddles centre; 7–10 are <em>late-side</em>. A dense
                wicket-cluster on one side reveals a directional exploit.
              </div>
              <StackedChartOrTable
                mode={viewMode}
                bucketLabel="Timing value"
                data={(extras?.byTimingValueBucket ?? []).map((r) => {
                  const lo = -1 + (r.bucket - 1) * 0.2;
                  return {
                    bucket: `${lo.toFixed(1)} to ${(lo + 0.2).toFixed(1)}`,
                    count: r.count,
                    type: wicketTypeLabel(r.wicketType),
                    rawType: r.wicketType,
                  };
                })}
              />
            </Col>

            <Col xs={24} lg={12}>
              <Text strong>Timing zone by delivery type</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Each delivery's mix of Perfect / Good / OK / Miss. Look for spin types whose stack
                collapses to OK or Miss — points to spin-recognition gaps.
              </div>
              <TimingZoneStackedChartOrTable
                mode={viewMode}
                bucketLabel="Delivery"
                axisRotate
                data={(extras?.timingByDelivery ?? [])
                  .filter((r) => r.shotTimingZone != null)
                  .map((r) => ({
                    bucket: deliveryName(r.deliveryTypeId),
                    zone:
                      SHOT_TIMING_ZONE[r.shotTimingZone as number] ?? `Zone ${r.shotTimingZone}`,
                    rawZone: r.shotTimingZone as number,
                    count: r.count,
                  }))}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>Timing zone by pace bucket</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Bucketed over 60-160 km/h (10 bins). Reaction-quality issues cluster at specific
                pace bands.
              </div>
              <TimingZoneStackedChartOrTable
                mode={viewMode}
                bucketLabel="Pace bucket"
                data={(extras?.timingByPaceBucket ?? [])
                  .filter((r) => r.shotTimingZone != null)
                  .map((r) => {
                    const lo = 60 + (r.bucket - 1) * 10;
                    return {
                      bucket: `${lo}-${lo + 10}`,
                      zone:
                        SHOT_TIMING_ZONE[r.shotTimingZone as number] ?? `Zone ${r.shotTimingZone}`,
                      rawZone: r.shotTimingZone as number,
                      count: r.count,
                    };
                  })}
              />
            </Col>

            <Col xs={24} lg={12}>
              <Text strong>Timing zone by spin intensity</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Bucketed over [0..1] (5 bins). Spin-heavy deliveries that collapse to OK / Miss
                point to weak spin-recognition logic.
              </div>
              <TimingZoneStackedChartOrTable
                mode={viewMode}
                bucketLabel="Spin bucket"
                data={(extras?.timingBySpinBucket ?? [])
                  .filter((r) => r.shotTimingZone != null)
                  .map((r) => {
                    const lo = (r.bucket - 1) * 0.2;
                    return {
                      bucket: `${lo.toFixed(1)}-${(lo + 0.2).toFixed(1)}`,
                      zone:
                        SHOT_TIMING_ZONE[r.shotTimingZone as number] ?? `Zone ${r.shotTimingZone}`,
                      rawZone: r.shotTimingZone as number,
                      count: r.count,
                    };
                  })}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Text strong>Timing zone by swing intensity</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Bucketed over [0..1] (5 bins). Swing-heavy balls that resolve to OK / Miss correlate
                with bowled/LBW exploits.
              </div>
              <TimingZoneStackedChartOrTable
                mode={viewMode}
                bucketLabel="Swing bucket"
                data={(extras?.timingBySwingBucket ?? [])
                  .filter((r) => r.shotTimingZone != null)
                  .map((r) => {
                    const lo = (r.bucket - 1) * 0.2;
                    return {
                      bucket: `${lo.toFixed(1)}-${(lo + 0.2).toFixed(1)}`,
                      zone:
                        SHOT_TIMING_ZONE[r.shotTimingZone as number] ?? `Zone ${r.shotTimingZone}`,
                      rawZone: r.shotTimingZone as number,
                      count: r.count,
                    };
                  })}
              />
            </Col>

            <Col xs={24}>
              <Text strong>Timing zone by batting shot zone</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Hot/Neutral/Cold (Unity <code>ZoneType</code>) × timing quality. <em>Miss</em>/
                <em>OK</em> inside a Hot zone is the strongest "AI logic gap" signal — shot
                selection and zone were correct, only the meter failed.
              </div>
              <TimingZoneStackedChartOrTable
                mode={viewMode}
                bucketLabel="Zone"
                data={(extras?.timingByBattingZone ?? [])
                  .filter((r) => r.shotTimingZone != null)
                  .map((r) => ({
                    bucket: BATTING_SHOT_ZONES[r.battingShotZone] ?? `Zone ${r.battingShotZone}`,
                    zone:
                      SHOT_TIMING_ZONE[r.shotTimingZone as number] ?? `Zone ${r.shotTimingZone}`,
                    rawZone: r.shotTimingZone as number,
                    count: r.count,
                  }))}
              />
            </Col>
          </Row>
        </Card>

        {/* ── 9. Raw events — gated behind explicit fetch (heavy, opt-in) ── */}
        <Card
          size="small"
          title={
            <SectionHeader
              title="All wickets — raw event log"
              help="Every column of every wicket-event row (30 fields, incl. the shot-timing meter — quality zone + direction + signed value). Heavy payload — fetched only when you click the button. Filter or period changes drop the loaded set so the next fetch returns fresh data."
            />
          }
          extra={
            events && (
              <Space size={6}>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setEventsPage({ page: 1, pageSize: eventsPage.pageSize });
                    void fetchEvents(1, eventsPage.pageSize);
                  }}
                  loading={eventsLoading}
                >
                  Reload
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setEvents(null);
                    setEventsPage({ page: 1, pageSize: 25 });
                    setEventsError(null);
                  }}
                >
                  Hide logs
                </Button>
              </Space>
            )
          }
        >
          {events == null ? (
            <div>
              {eventsError && (
                <Alert
                  type="error"
                  showIcon
                  closable
                  message={eventsError}
                  onClose={() => setEventsError(null)}
                  style={{ marginBottom: 16 }}
                />
              )}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '32px 16px',
                  background: '#fafafa',
                  border: '1px dashed #d9d9d9',
                  borderRadius: 6,
                }}
              >
                <DatabaseOutlined style={{ fontSize: 40, color: '#bfbfbf', marginBottom: 12 }} />
                <Text strong style={{ fontSize: 14, marginBottom: 4 }}>
                  Raw wicket logs are not loaded
                </Text>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    marginBottom: 16,
                    textAlign: 'center',
                    maxWidth: 420,
                  }}
                >
                  Up to 500 rows × 29 columns per page. Fetched on demand to keep the dashboard fast
                  — filter / period / user changes clear the loaded set.
                </Text>
                <Button
                  type="primary"
                  loading={eventsLoading}
                  onClick={() => {
                    setEventsPage({ page: 1, pageSize: 25 });
                    void fetchEvents(1, 25);
                  }}
                >
                  Get raw wicket logs
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Showing {events.events.length} of {fmt(events.total)} events.
              </Text>
              <Table<WicketEventRow>
                size="small"
                scroll={{ x: 2810 }}
                dataSource={events.events}
                loading={eventsLoading}
                rowKey="id"
                pagination={{
                  current: eventsPage.page,
                  pageSize: eventsPage.pageSize,
                  total: events.total,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '25', '50', '100'],
                  onChange: (page, pageSize) => {
                    setEventsPage({ page, pageSize });
                    void fetchEvents(page, pageSize);
                  },
                  size: 'small',
                }}
                columns={[
                  {
                    title: 'When',
                    dataIndex: 'createdAt',
                    width: 160,
                    fixed: 'left',
                    render: (v: string) => new Date(v).toLocaleString(),
                  },
                  {
                    title: 'Type',
                    dataIndex: 'wicketType',
                    width: 110,
                    fixed: 'left',
                    render: (v: string) => <Tag color={wicketColor(v)}>{wicketTypeLabel(v)}</Tag>,
                  },
                  {
                    title: 'Match',
                    dataIndex: 'matchId',
                    width: 150,
                    render: (v: string | null) => <CopyableId value={v} previewLength={8} />,
                  },
                  {
                    title: 'Inn',
                    dataIndex: 'innings',
                    width: 60,
                    align: 'center',
                  },
                  {
                    title: 'Over.Ball',
                    width: 90,
                    render: (_v, r) => `${r.over + 1}.${r.ball + 1}`,
                  },
                  {
                    title: 'aidiff',
                    dataIndex: 'aidiff',
                    width: 80,
                    align: 'right',
                    render: (v: number | null) => (v == null ? '-' : v),
                  },
                  {
                    title: 'Division',
                    dataIndex: 'division',
                    width: 160,
                    render: (v: number | null) =>
                      v == null ? <Text type="secondary">-</Text> : <Tag>{divisionName(v)}</Tag>,
                  },
                  {
                    title: 'Batter slot',
                    dataIndex: 'batsmanIndex',
                    width: 100,
                    align: 'right',
                    render: (v: number) => `#${v + 1}`,
                  },
                  {
                    title: 'Bowler slot',
                    dataIndex: 'bowlerIndex',
                    width: 100,
                    align: 'right',
                    render: (v: number) => `#${v + 1}`,
                  },
                  {
                    title: 'Shot',
                    dataIndex: 'shotId',
                    width: 320,
                    render: (v: number | null) =>
                      v == null ? (
                        <Text type="secondary">-</Text>
                      ) : (
                        <Space size={4} wrap={false} style={{ maxWidth: '100%' }}>
                          <Tooltip title={`shotId ${v}`}>
                            <Tag
                              style={{
                                margin: 0,
                                maxWidth: 220,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'inline-block',
                                verticalAlign: 'middle',
                              }}
                            >
                              {shotName(v)}
                            </Tag>
                          </Tooltip>
                          <CopyableId
                            value={String(v)}
                            previewLength={String(v).length}
                            showEllipsis={false}
                          />
                        </Space>
                      ),
                  },
                  {
                    title: 'Elev',
                    dataIndex: 'shotType',
                    width: 90,
                    render: (v: number | null) =>
                      v == null ? (
                        <Text type="secondary">-</Text>
                      ) : (
                        <Tag>{SHOT_TYPES[v] ?? `Type ${v}`}</Tag>
                      ),
                  },
                  {
                    title: 'Timing Zone',
                    dataIndex: 'shotTimingZone',
                    width: 110,
                    render: (v: number | null) =>
                      v == null ? (
                        <Text type="secondary">-</Text>
                      ) : (
                        <Tag color={TIMING_ZONE_COLORS[v] ?? 'default'}>
                          {shotTimingZoneName(v)}
                        </Tag>
                      ),
                  },
                  {
                    title: 'Timing Dir',
                    dataIndex: 'shotTimingDirection',
                    width: 100,
                    render: (v: number | null) =>
                      v == null ? (
                        <Text type="secondary">-</Text>
                      ) : (
                        <Tag color={TIMING_DIRECTION_COLORS[v] ?? 'default'}>
                          {shotTimingDirectionName(v)}
                        </Tag>
                      ),
                  },
                  {
                    title: 'Timing Value',
                    dataIndex: 'shotTimingValue',
                    width: 110,
                    align: 'right',
                    render: (v: number | null) =>
                      v == null ? (
                        <Text type="secondary">-</Text>
                      ) : (
                        <span
                          style={{
                            color: v < 0 ? '#fa8c16' : v > 0 ? '#cf1322' : '#52c41a',
                          }}
                        >
                          {v > 0 ? '+' : ''}
                          {v.toFixed(2)}
                        </span>
                      ),
                  },
                  {
                    title: 'Zone',
                    dataIndex: 'battingShotZone',
                    width: 100,
                    render: (v: number) => (
                      <Tag color={v === 1 ? 'red' : v === 2 ? 'blue' : 'default'}>
                        {BATTING_SHOT_ZONES[v] ?? `Zone ${v}`}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Delivery',
                    dataIndex: 'deliveryTypeId',
                    width: 180,
                    render: (v: number) => (
                      <>
                        <Tag>{deliveryName(v)}</Tag>
                        <DeliveryFamilyTag deliveryTypeId={v} />
                      </>
                    ),
                  },
                  {
                    title: 'Pace',
                    dataIndex: 'bowlingPace',
                    width: 80,
                    align: 'right',
                    render: (v: number) => v.toFixed(1),
                  },
                  {
                    title: 'Spin',
                    dataIndex: 'bowlingSpinIntensity',
                    width: 80,
                    align: 'right',
                    render: (v: number) => v.toFixed(2),
                  },
                  {
                    title: 'Swing',
                    dataIndex: 'bowlingSwingIntensity',
                    width: 80,
                    align: 'right',
                    render: (v: number) => v.toFixed(2),
                  },
                  {
                    title: 'Bounce X / Z',
                    width: 130,
                    render: (_v, r) => `${r.ballBounceX.toFixed(2)}, ${r.ballBounceZ.toFixed(2)}`,
                  },
                  {
                    title: 'Batter X / Z',
                    width: 130,
                    render: (_v, r) =>
                      `${r.batsmanPositionX.toFixed(2)}, ${r.batsmanPositionZ.toFixed(2)}`,
                  },
                  {
                    title: 'Shot angle',
                    dataIndex: 'shotAngleDegrees',
                    width: 110,
                    align: 'right',
                    render: (v: number | null) => (v == null ? '-' : `${v.toFixed(1)}°`),
                  },
                  {
                    title: 'Fielding dir',
                    dataIndex: 'wicketLocationZone',
                    width: 130,
                    render: (v: number | null) =>
                      v == null ? <Text type="secondary">-</Text> : <Tag>{zoneName(v)}</Tag>,
                  },
                  {
                    title: 'Fielder start',
                    width: 140,
                    render: (_v, r) =>
                      r.fielderStartPositionX == null
                        ? '-'
                        : `${r.fielderStartPositionX.toFixed(1)}, ${r.fielderStartPositionZ?.toFixed(1) ?? '-'}`,
                  },
                  {
                    title: 'Fielder catch',
                    width: 140,
                    render: (_v, r) =>
                      r.fielderCatchPositionX == null
                        ? '-'
                        : `${r.fielderCatchPositionX.toFixed(1)}, ${r.fielderCatchPositionZ?.toFixed(1) ?? '-'}`,
                  },
                ]}
              />
            </>
          )}
        </Card>

        <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 8 }}>
          Note: most lookup IDs (shot, delivery, zone) are not yet on the Unity wire — the build
          ships them only as raw integers today. Names rendered above come from the client-mirrored
          mappers in <code>src/constants/wicketConstants.ts</code>.
        </Paragraph>
      </Space>
    </Spin>
  );
};
